import MetaMaskSDK from "@metamask/sdk";
import type { Wallet } from "@/app/types/wallet";
import {
  clearWalletData,
  getStoredWalletData,
  storeWalletData,
} from "./walletUtils";
import { WalletProviderType } from "../services/enum/wallet-provider-type";
import { MetamaskErrorCodes } from "../services/enum/metamask-error-codes";
import { ethers } from "ethers";
import { getPartnerCode } from "../services/service/localStorageService";
import { ExternalProvider } from "@ethersproject/providers";
import getDeviceType from "../device/getDeviceType";

type Eip1193Provider = ExternalProvider & {
  request?: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
};

export const metamask = new MetaMaskSDK({
  dappMetadata: {
    name: "Sleap.io",
    url:
      typeof window !== "undefined"
        ? window.location.origin
        : "https://gatetravel.sleap.io",
  },
  infuraAPIKey: process.env.INFURA_PROJECT_ID,
});

export class MetaMask implements Wallet {
  activeProvider: undefined | ethers.providers.Web3Provider;

  async connect() {
    const provider = metamask.getProvider();
    if (!provider) throw new Error("No MetaMask provider found");

    //@ts-ignore
    const accounts: string[] = await provider.request({
      method: "eth_requestAccounts",
    });

    const walletId = accounts[0];
    return this.persistLastUsedWallet(WalletProviderType.METAMASK, walletId);
  }

  async disconnect(): Promise<void> {
    try {
      await metamask.disconnect();
    } catch (error) {
      console.warn(error);
    }
  }

  async signTransaction(
    walletId: string,
    challengeMessage: string
  ): Promise<string> {
    try {
      const provider = metamask.getProvider();
      if (!provider) {
        return Promise.reject("No MetaMask provider found");
      }

      // Ensure account is authorized before requesting a signature.
      await provider.request({ method: "eth_requestAccounts" });

      const signature = (await provider.request({
        method: "personal_sign",
        params: [challengeMessage, walletId],
      })) as string;

      return Promise.resolve(signature);
    } catch (e) {
      return Promise.reject("unable to sign Message");
    }
  }
  async isConnected() {
    const walletProviderType = WalletProviderType.METAMASK;
    const { walletAddress } = getStoredWalletData();

    if (walletAddress && walletProviderType) {
      const { isMobileType, isTabletType } = getDeviceType();
      const isMobile = isMobileType || isTabletType;
      const injectedProvider = this.getWalletProvider(
        walletProviderType,
        isMobile
      ) as Eip1193Provider | undefined;

      if (injectedProvider?.request) {
        const accounts = (await injectedProvider
          .request({ method: "eth_accounts" })
          .catch(() => [])) as string[];
        if (accounts?.[0]) {
          const provider = new ethers.providers.Web3Provider(injectedProvider);
          this.listenToEvents(provider);
          return { accounts, providerType: walletProviderType };
        }
      }
    }

    return "";
  }

  async getAddress() {
    const ethereum = metamask.getProvider();
    const address = ethereum?.getSelectedAddress();
    return address ? address : "";
  }

  persistLastUsedWallet = (
    walletProviderType?: WalletProviderType,
    walletAddress?: string
  ) => {
    if (!walletProviderType || !walletAddress) {
      clearWalletData();
      return "";
    } else {
      const walletId = getStoredWalletData().walletAddress;
      const walletType = getStoredWalletData().walletProviderType;
      if (walletId === walletAddress && walletType === walletProviderType) {
        return walletId;
      }

      storeWalletData(walletProviderType, walletAddress!);

      return walletAddress;
    }
  };

  async listenToEvents(provider: ethers.providers.Web3Provider) {
    if (this.activeProvider) {
      // Remove existing listeners
      this.activeProvider?.removeAllListeners("accountsChanged");
      this.activeProvider?.removeAllListeners("disconnect");
    }

    // Set new provider and add listeners
    this.activeProvider = provider;

    this.activeProvider?.addListener(
      "accountsChanged",
      this.onAccountsChanged.bind(this)
    );
    this.activeProvider?.addListener(
      "disconnect",
      this.persistLastUsedWallet.bind(this)
    );
  }

  onAccountsChanged(res: string[]) {
    const walletAddress = res?.[0] ?? null;
    if (!walletAddress) {
      this.persistLastUsedWallet();
    }
  }

  getProviderOrRedirect(
    walletProviderType: WalletProviderType,
    isMobile: boolean
  ) {
    let url = "";
    const sleapUrl = this.getBaseUrl();
    const searchParams = this.getParamsForSearch();
    let startSearch = "";
    const partnerCode = getPartnerCode();
    if (partnerCode) {
      startSearch = `?partner=${partnerCode}&startSearch=`;
    } else {
      startSearch = "?startSearch=";
    }

    const provider = this.getWalletProvider(walletProviderType, isMobile);
    if (!provider) {
      url = this.getWalletUrl(
        url,
        walletProviderType,
        isMobile,
        searchParams,
        sleapUrl,
        startSearch
      );
      window.open(url, isMobile ? "_self" : "_blank");
      return null;
    } else {
      return new ethers.providers.Web3Provider(provider);
    }
  }

  // Function to get params for search
  getParamsForSearch(): string | undefined {
    let searchParams = undefined;
    return searchParams;
  }

  // Function to get redirect URL
  getBaseUrl() {
    return window.location.href;
  }

  getWalletUrl(
    url: string,
    walletProviderType: string,
    isMobile: boolean,
    searchParams: string | undefined,
    sleapUrl: string,
    startSearch: string
  ) {
    url = this.getRedirectUrl(
      isMobile,
      walletProviderType,
      searchParams,
      sleapUrl,
      startSearch
    );

    return url;
  }

  getRedirectUrl(
    isMobile: boolean,
    walletProviderType: string,
    searchParams: string | undefined,
    sleapUrl: string,
    startSearch: string
  ) {
    let url;
    if (isMobile) {
      url = `https://metamask.app.link/dapp/${sleapUrl}${startSearch}${walletProviderType}`;
      if (searchParams) {
        url = url + searchParams;
      }
    } else {
      url = "https://metamask.io/download.html";
    }
    return url;
  }

  getWalletProvider(walletProvider: WalletProviderType, isMobile: boolean) {
    let provider: ExternalProvider | undefined;
    // @ts-ignore
    // @ts-ignore
    if (window?.ethereum?.providers?.length) {
      // @ts-ignore
      window.ethereum.providers.forEach(async (p) => {
        switch (walletProvider) {
          case WalletProviderType.METAMASK:
            if (p.isMetaMask) provider = p;
            break;
          default:
            return null;
        }
        return provider;
      });
    } else {
      switch (walletProvider) {
        case WalletProviderType.METAMASK:
          // @ts-ignore
          provider = this.getMetamaskProviderForBoth();
          break;
      }
    }
    return provider;
  }

  getMetamaskProviderForBoth() {
    let provider;
    // @ts-ignore
    if (window?.ethereum?.isMetaMask) provider = window.ethereum;
    return provider;
  }

  handleWalletError(errorCode: string): string {
    let error = "";
    switch (errorCode) {
      case MetamaskErrorCodes.NO_METAMASK_PLUGIN: {
        error = "No metamask plugin found.";
        break;
      }
      case "ACTION_REJECTED": {
        error = "You rejected the transaction.";
        break;
      }
      case MetamaskErrorCodes.NO_WALLET_CONNECTED: {
        error = "You rejected the transaction.";
        break;
      }
      case MetamaskErrorCodes.WALLET_DISCONNECTED: {
        error = "Wallet has been disconnected.";
        break;
      }
      default: {
        error = "Unknown error while connecting your metamask wallet";
      }
    }
    return error;
  }

  getConnectedWallet(): WalletProviderType {
    return WalletProviderType.METAMASK;
  }
}
